/**
 * Release Categorization Service
 * 
 * Main service for categorizing releases into swimlanes based on keyword matching
 * and precedence rules. Processes ProjectVersion data into CategorizedRelease objects.
 */

import { ProjectVersion } from './apiService'
import {
  ReleaseCategory,
  CategorizedRelease,
  CategorizationResult,
  SwimLaneConfig,
  RELEASE_KEYWORDS,
  CROSS_CATEGORY_KEYWORDS
} from '../types/release'
import { resolveCategoryConflicts } from '../utils/releasePrecedenceRules'
import { normalizeText } from '../utils/releaseKeywordMatcher'

// ============================================================================
// SWIMLANE CONFIGURATION
// ============================================================================

/**
 * Default swimlane configurations
 */
export const DEFAULT_SWIMLANE_CONFIGS: SwimLaneConfig[] = [
  {
    category: ReleaseCategory.OSS,
    label: 'OSS',
    index: 0,
    color: '#9C27B0',           // Purple
    backgroundColor: '#F3E5F5',  // Light purple
    textColor: '#4A148C',       // Dark purple
    keywords: RELEASE_KEYWORDS[ReleaseCategory.OSS],
    visible: true
  },
  {
    category: ReleaseCategory.SOFTWARE,
    label: 'Software',
    index: 1,
    color: '#2196F3',           // Blue
    backgroundColor: '#E3F2FD',  // Light blue
    textColor: '#0D47A1',       // Dark blue
    keywords: RELEASE_KEYWORDS[ReleaseCategory.SOFTWARE],
    visible: true
  },
  {
    category: ReleaseCategory.CLOUD,
    label: 'Cloud',
    index: 2,
    color: '#4CAF50',           // Green
    backgroundColor: '#E8F5E8',  // Light green
    textColor: '#1B5E20',       // Dark green
    keywords: RELEASE_KEYWORDS[ReleaseCategory.CLOUD],
    visible: true
  },
  {
    category: ReleaseCategory.AZURE,
    label: 'Azure',
    index: 3,
    color: '#FF9800',           // Orange
    backgroundColor: '#FFF3E0',  // Light orange
    textColor: '#E65100',       // Dark orange
    keywords: RELEASE_KEYWORDS[ReleaseCategory.AZURE],
    visible: true
  }
]

// ============================================================================
// CATEGORIZATION SERVICE CLASS
// ============================================================================

export class ReleaseCategorizationService {
  private swimlaneConfigs: SwimLaneConfig[]
  
  constructor(customConfigs?: SwimLaneConfig[]) {
    this.swimlaneConfigs = customConfigs || DEFAULT_SWIMLANE_CONFIGS
  }
  
  /**
   * Categorize a single release (legacy method - use categorizeSingleRelease for cross-category support)
   */
  categorizeRelease(
    release: ProjectVersion,
    options: {
      exactWord?: boolean
      caseSensitive?: boolean
      minConfidence?: number
      includeDescription?: boolean
    } = {}
  ): CategorizedRelease {
    const { includeDescription = true } = options

    // Combine name and description for analysis
    let analysisText = release.name || ''
    if (includeDescription && release.description) {
      analysisText += ' ' + release.description
    }

    // Perform categorization
    const result = resolveCategoryConflicts(analysisText, options)

    // Create categorized release
    const categorizedRelease: CategorizedRelease = {
      ...release,
      category: result.category,
      swimlaneIndex: result.category ? this.getCategoryIndex(result.category) : undefined,
      matchedKeywords: result.matchedKeywords,
      isCategorized: result.success,
      displayName: this.createDisplayName(release),
      displayDescription: this.createDisplayDescription(release)
    }

    return categorizedRelease
  }

  /**
   * Categorize a single release with cross-category support
   * Returns an array of releases (multiple entries for cross-category releases)
   */
  categorizeSingleRelease(
    release: ProjectVersion,
    options: {
      exactWord?: boolean
      caseSensitive?: boolean
      minConfidence?: number
      includeDescription?: boolean
    } = {}
  ): CategorizedRelease[] {
    const { includeDescription = true } = options

    // Combine name and description for analysis
    let analysisText = release.name || ''
    if (includeDescription && release.description) {
      analysisText += ' ' + release.description
    }

    // Check for cross-category keywords
    const crossCategoryMatches = this.findCrossCategoryMatches(analysisText)

    if (crossCategoryMatches.length > 0) {
      // Create duplicate releases for each matching category
      return this.createCrossCategoryReleases(release, crossCategoryMatches, analysisText, options)
    } else {
      // Standard single-category categorization
      const result = resolveCategoryConflicts(analysisText, options)

      const categorizedRelease: CategorizedRelease = {
        ...release,
        category: result.category,
        swimlaneIndex: result.category ? this.getCategoryIndex(result.category) : undefined,
        matchedKeywords: result.matchedKeywords,
        isCategorized: result.success,
        displayName: this.createDisplayName(release),
        displayDescription: this.createDisplayDescription(release)
      }

      return [categorizedRelease]
    }
  }
  
  /**
   * Categorize multiple releases (legacy method - use categorizeReleasesWithCrossCategory for cross-category support)
   */
  categorizeReleases(
    releases: ProjectVersion[],
    options: {
      exactWord?: boolean
      caseSensitive?: boolean
      minConfidence?: number
      includeDescription?: boolean
      filterUncategorized?: boolean
    } = {}
  ): CategorizedRelease[] {
    const { filterUncategorized = false } = options

    const categorized = releases.map(release =>
      this.categorizeRelease(release, options)
    )

    if (filterUncategorized) {
      return categorized.filter(release => release.isCategorized)
    }

    return categorized
  }

  /**
   * Categorize multiple releases with cross-category support
   * Returns flattened array including duplicated cross-category releases
   */
  categorizeReleasesWithCrossCategory(
    releases: ProjectVersion[],
    options: {
      exactWord?: boolean
      caseSensitive?: boolean
      minConfidence?: number
      includeDescription?: boolean
      filterUncategorized?: boolean
    } = {}
  ): CategorizedRelease[] {
    const { filterUncategorized = false } = options

    // Process each release and flatten the results
    const categorized = releases.flatMap(release =>
      this.categorizeSingleRelease(release, options)
    )

    if (filterUncategorized) {
      return categorized.filter(release => release.isCategorized)
    }

    return categorized
  }
  
  /**
   * Get categorization statistics
   */
  getCategorizationStats(categorizedReleases: CategorizedRelease[]): {
    total: number
    categorized: number
    uncategorized: number
    byCategory: Record<ReleaseCategory, number>
    categorizationRate: number
  } {
    const total = categorizedReleases.length
    const categorized = categorizedReleases.filter(r => r.isCategorized).length
    const uncategorized = total - categorized
    
    const byCategory: Record<ReleaseCategory, number> = {
      [ReleaseCategory.SOFTWARE]: 0,
      [ReleaseCategory.CLOUD]: 0,
      [ReleaseCategory.AZURE]: 0,
      [ReleaseCategory.OSS]: 0
    }
    
    categorizedReleases.forEach(release => {
      if (release.category) {
        byCategory[release.category]++
      }
    })
    
    return {
      total,
      categorized,
      uncategorized,
      byCategory,
      categorizationRate: total > 0 ? categorized / total : 0
    }
  }
  
  /**
   * Filter releases by date (September 2025 onwards)
   */
  filterByDate(
    releases: CategorizedRelease[],
    minDate: Date = new Date('2025-09-01')
  ): CategorizedRelease[] {
    return releases.filter(release => {
      if (!release.releaseDate) {
        // Include unreleased versions
        return !release.released
      }
      
      const releaseDate = new Date(release.releaseDate)
      return releaseDate >= minDate
    })
  }
  
  /**
   * Sort releases by date and category
   */
  sortReleases(
    releases: CategorizedRelease[],
    sortBy: 'date' | 'category' | 'name' = 'date'
  ): CategorizedRelease[] {
    return [...releases].sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return this.compareDates(a, b)
        case 'category':
          return this.compareCategories(a, b)
        case 'name':
          return a.displayName.localeCompare(b.displayName)
        default:
          return 0
      }
    })
  }
  
  /**
   * Get swimlane configuration for a category
   */
  getSwimLaneConfig(category: ReleaseCategory): SwimLaneConfig | undefined {
    return this.swimlaneConfigs.find(config => config.category === category)
  }
  
  /**
   * Get all swimlane configurations
   */
  getAllSwimLaneConfigs(): SwimLaneConfig[] {
    return [...this.swimlaneConfigs]
  }
  
  /**
   * Update swimlane configuration
   */
  updateSwimLaneConfig(category: ReleaseCategory, updates: Partial<SwimLaneConfig>): void {
    const index = this.swimlaneConfigs.findIndex(config => config.category === category)
    if (index !== -1) {
      this.swimlaneConfigs[index] = { ...this.swimlaneConfigs[index], ...updates }
    }
  }
  
  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  private getCategoryIndex(category: ReleaseCategory): number {
    const config = this.getSwimLaneConfig(category)
    return config?.index ?? -1
  }

  /**
   * Find cross-category keyword matches in text
   */
  private findCrossCategoryMatches(text: string): Array<{ keyword: string; categories: ReleaseCategory[] }> {
    const normalizedText = normalizeText(text)
    const matches: Array<{ keyword: string; categories: ReleaseCategory[] }> = []

    for (const [keyword, categories] of Object.entries(CROSS_CATEGORY_KEYWORDS)) {
      if (normalizedText.toLowerCase().includes(keyword.toLowerCase())) {
        matches.push({ keyword, categories })
      }
    }

    return matches
  }

  /**
   * Create duplicate releases for cross-category keywords
   */
  private createCrossCategoryReleases(
    release: ProjectVersion,
    crossMatches: Array<{ keyword: string; categories: ReleaseCategory[] }>,
    analysisText: string,
    options: any
  ): CategorizedRelease[] {
    const releases: CategorizedRelease[] = []

    // Get all unique categories from cross-matches
    const allCategories = new Set<ReleaseCategory>()
    const matchedKeywords = new Set<string>()

    crossMatches.forEach(match => {
      matchedKeywords.add(match.keyword)
      match.categories.forEach(category => allCategories.add(category))
    })

    const categoriesArray = Array.from(allCategories)

    // Create a release for each category
    categoriesArray.forEach(category => {
      const categorizedRelease: CategorizedRelease = {
        ...release,
        id: `${release.id}_${category}`, // Create unique ID for each duplicate
        category,
        swimlaneIndex: this.getCategoryIndex(category),
        matchedKeywords: Array.from(matchedKeywords),
        isCategorized: true,
        displayName: this.createDisplayName(release),
        displayDescription: this.createDisplayDescription(release),
        allCategories: categoriesArray,
        isCrossCategoryDuplicate: true,
        originalReleaseId: release.id
      }

      releases.push(categorizedRelease)
    })

    return releases
  }
  
  private createDisplayName(release: ProjectVersion): string {
    return release.name || `Release ${release.id}`
  }
  
  private createDisplayDescription(release: ProjectVersion): string | undefined {
    if (!release.description) return undefined
    
    // Truncate long descriptions
    const maxLength = 100
    if (release.description.length <= maxLength) {
      return release.description
    }
    
    return release.description.substring(0, maxLength - 3) + '...'
  }
  
  private compareDates(a: CategorizedRelease, b: CategorizedRelease): number {
    // Handle unreleased versions
    if (!a.releaseDate && !b.releaseDate) {
      return a.displayName.localeCompare(b.displayName)
    }
    if (!a.releaseDate) return 1  // Unreleased comes after released
    if (!b.releaseDate) return -1
    
    const dateA = new Date(a.releaseDate)
    const dateB = new Date(b.releaseDate)
    return dateA.getTime() - dateB.getTime()
  }
  
  private compareCategories(a: CategorizedRelease, b: CategorizedRelease): number {
    // Uncategorized items go to the end
    if (!a.category && !b.category) return 0
    if (!a.category) return 1
    if (!b.category) return -1
    
    const indexA = a.swimlaneIndex ?? 999
    const indexB = b.swimlaneIndex ?? 999
    
    if (indexA !== indexB) {
      return indexA - indexB
    }
    
    // Same category, sort by date
    return this.compareDates(a, b)
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

/**
 * Default categorization service instance
 */
export const releaseCategorizationService = new ReleaseCategorizationService()

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Quick categorization function for single release
 */
export function categorizeRelease(release: ProjectVersion): CategorizedRelease {
  return releaseCategorizationService.categorizeRelease(release)
}

/**
 * Quick categorization function for multiple releases (legacy - single category per release)
 */
export function categorizeReleases(releases: ProjectVersion[]): CategorizedRelease[] {
  return releaseCategorizationService.categorizeReleases(releases)
}

/**
 * Quick categorization function for multiple releases with cross-category support
 */
export function categorizeReleasesWithCrossCategory(releases: ProjectVersion[]): CategorizedRelease[] {
  return releaseCategorizationService.categorizeReleasesWithCrossCategory(releases)
}

/**
 * Filter and categorize releases for timeline display (legacy - single category per release)
 */
export function prepareReleasesForTimeline(
  releases: ProjectVersion[],
  options: {
    minDate?: Date
    filterUncategorized?: boolean
    sortBy?: 'date' | 'category' | 'name'
  } = {}
): CategorizedRelease[] {
  const {
    minDate = new Date('2025-09-01'),
    filterUncategorized = true,
    sortBy = 'date'
  } = options

  // Categorize releases
  let categorized = releaseCategorizationService.categorizeReleases(releases, {
    filterUncategorized
  })

  // Filter by date
  categorized = releaseCategorizationService.filterByDate(categorized, minDate)

  // Sort releases
  categorized = releaseCategorizationService.sortReleases(categorized, sortBy)

  return categorized
}

/**
 * Filter and categorize releases for timeline display with cross-category support
 */
export function prepareReleasesForTimelineWithCrossCategory(
  releases: ProjectVersion[],
  options: {
    minDate?: Date
    filterUncategorized?: boolean
    sortBy?: 'date' | 'category' | 'name'
  } = {}
): CategorizedRelease[] {
  const {
    minDate = new Date('2025-09-01'),
    filterUncategorized = true,
    sortBy = 'date'
  } = options

  // Categorize releases with cross-category support
  let categorized = releaseCategorizationService.categorizeReleasesWithCrossCategory(releases, {
    filterUncategorized
  })

  // Filter by date
  categorized = releaseCategorizationService.filterByDate(categorized, minDate)

  // Sort releases
  categorized = releaseCategorizationService.sortReleases(categorized, sortBy)

  return categorized
}
